function createIframePopupContainer(url: string): HTMLElement {
  const safeUrl = toSafeIframeUrl(url);

  const container = document.createElement("div");
  container.className = "iframe-popup-container";

  const popup = document.createElement("div");
  popup.className = "iframe-popup";

  const button = document.createElement("button");
  button.className = "close-button";
  button.type = "button";
  button.setAttribute("aria-label", "Close");

  const img = document.createElement("img");
  img.src = "/assets/icons/close.svg";
  img.alt = "Close";
  button.append(img);

  const wrapper = document.createElement("div");
  wrapper.className = "iframe-wrapper";

  const iframe = document.createElement("iframe");
  iframe.loading = "lazy";
  iframe.allowFullscreen = true;
  iframe.referrerPolicy = "no-referrer";
  iframe.sandbox.add("allow-scripts", "allow-same-origin", "allow-forms", "allow-popups");

  if (safeUrl) {
    iframe.dataset.src = safeUrl; // load on open
  }

  wrapper.append(iframe);
  popup.append(button, wrapper);
  container.append(popup);

  return container;
}

function toSafeIframeUrl(rawUrl: string): string | null {
  try {
      const parsed = new URL(rawUrl, window.location.origin);
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
          return null;
        }
      return parsed.toString();
    } catch {
      return null;
    }
}

export default function initIframePopup() {
  // Handle standalone iframe popup blocks
  const iframeBlocks = document.querySelectorAll(".iframe-popup-block");

  iframeBlocks.forEach((iframeBlock) => {
    const iframePopupContainer = iframeBlock.querySelector<HTMLElement>(
      ".iframe-popup-container"
    );
    const iframe = iframeBlock.querySelector<HTMLIFrameElement>("iframe");
    const closeButton = iframeBlock.querySelector<HTMLButtonElement>(".close-button");

    if (!iframePopupContainer || !iframe) {
      return;
    }

    // Move popup container to body for proper overlay
    document.body.append(iframePopupContainer);

    const openPopup = () => {
      iframePopupContainer.classList.add("active");
      
      // Load iframe src on open for better performance
      const dataSrc = iframe.dataset.src;
      if (dataSrc && !iframe.src) {
        iframe.src = dataSrc;
      }
      
      // Prevent body scroll when popup is open
      document.body.style.overflow = "hidden";
    };

    const closePopup = () => {
      iframePopupContainer.classList.remove("active");
      document.body.style.overflow = "";
    };

    // Open popup when clicking on the block
    iframeBlock.addEventListener("click", (e) => {
      if (e.target === iframePopupContainer) return;
      openPopup();
    });

    // Close popup when clicking on the overlay (not the iframe itself)
    iframePopupContainer.addEventListener("click", (e) => {
      if (e.target === iframePopupContainer) {
        closePopup();
      }
    });

    // Close button click handler
    if (closeButton) {
      closeButton.addEventListener("click", (e) => {
        e.stopPropagation();
        closePopup();
      });
    }

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && iframePopupContainer.classList.contains("active")) {
        closePopup();
      }
    });
  });

  // Handle item blocks with iframe popup functionality
  const itemBlocksWithIframe = document.querySelectorAll<HTMLElement>(".has-iframe-popup");

  itemBlocksWithIframe.forEach((itemBlock) => {
    const iframeUrl = itemBlock.dataset.iframeUrl;
    
    if (!iframeUrl) {
      return;
    }

    // Create and append popup container
    const iframePopupContainer = createIframePopupContainer(iframeUrl);
    document.body.append(iframePopupContainer);

    const iframe = iframePopupContainer.querySelector<HTMLIFrameElement>("iframe");
    const closeButton = iframePopupContainer.querySelector<HTMLButtonElement>(".close-button");

    const openPopup = () => {
      iframePopupContainer.classList.add("active");
      
      // Load iframe src on open for better performance
      if (iframe) {
        const dataSrc = iframe.dataset.src;
        if (dataSrc && !iframe.src) {
          iframe.src = dataSrc;
        }
      }
      
      // Prevent body scroll when popup is open
      document.body.style.overflow = "hidden";
    };

    const closePopup = () => {
      iframePopupContainer.classList.remove("active");
      document.body.style.overflow = "";
    };

    // Make item block clickable
    itemBlock.style.cursor = "pointer";
    
    itemBlock.addEventListener("click", (e) => {
      e.preventDefault();
      openPopup();
    });

    // Close popup when clicking on the overlay
    iframePopupContainer.addEventListener("click", (e) => {
      if (e.target === iframePopupContainer) {
        closePopup();
      }
    });

    // Close button click handler
    if (closeButton) {
      closeButton.addEventListener("click", (e) => {
        e.stopPropagation();
        closePopup();
      });
    }

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && iframePopupContainer.classList.contains("active")) {
        closePopup();
      }
    });
  });
}

